import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, Building2, User, FileText } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { prestacoesApi, unidadesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { Unidade } from '../types';

export function NovaPrestacaoPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [selectedUnidade, setSelectedUnidade] = useState<Unidade | null>(null);
  
  const [formData, setFormData] = useState({
    unidade_id: '',
    ano: new Date().getFullYear(),
    mes: new Date().getMonth() + 1,
    responsavel_nome: user?.display_name || '',
    responsavel_cpf: '',
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    loadUnidades();
  }, []);

  const loadUnidades = async () => {
    try {
      setLoading(true);
      const data = await unidadesApi.list();
      setUnidades(data);
    } catch (error) {
      console.error('Error loading unidades:', error);
      alert('Erro ao carregar unidades');
    } finally {
      setLoading(false);
    }
  };

  const handleUnidadeChange = (unidadeId: string) => {
    const unidade = unidades.find(u => u.id === parseInt(unidadeId));
    setSelectedUnidade(unidade || null);
    setFormData(prev => ({ ...prev, unidade_id: unidadeId }));
    
    // Limpar erro da unidade se existir
    if (errors.unidade_id) {
      setErrors(prev => ({ ...prev, unidade_id: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.unidade_id) {
      newErrors.unidade_id = 'Selecione uma unidade';
    }
    if (!formData.ano || formData.ano < 2020 || formData.ano > 2030) {
      newErrors.ano = 'Ano inválido';
    }
    if (!formData.mes || formData.mes < 1 || formData.mes > 12) {
      newErrors.mes = 'Mês inválido';
    }
    if (!formData.responsavel_nome.trim()) {
      newErrors.responsavel_nome = 'Nome do responsável é obrigatório';
    }
    if (!formData.responsavel_cpf.trim()) {
      newErrors.responsavel_cpf = 'CPF do responsável é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Verificar se já existe prestação para esta unidade/ano/mês
    try {
      setSalvando(true);
      
      // Buscar prestações existentes para verificar duplicata
      const existingPrestacoes = await prestacoesApi.list(formData.ano, parseInt(formData.unidade_id));
      
      if (!!existingPrestacoes) {
        const duplicata = existingPrestacoes.find(
          (p: any) => p.mes === formData.mes
        );
        
        if (duplicata) {
          setErrors({ 
            mes: `Já existe uma prestação de contas para ${formData.mes}/${formData.ano} desta unidade` 
          });
          setSalvando(false);
          return;
        }
      }

      const result = await prestacoesApi.create({
        unidade_id: parseInt(formData.unidade_id),
        ano: formData.ano,
        mes: formData.mes,
        responsavel_nome: formData.responsavel_nome,
        responsavel_cpf: formData.responsavel_cpf,
      });

      console.log('Prestação criada:', result);

      // Redirecionar diretamente para adicionar documentos
      if (result && result.id && typeof result.id === 'number') {
        navigate(`/prestacoes/${result.id}/documentos/novo`);
      } else {
        // Fallback: voltar para lista de prestações se não conseguir o ID
        alert('Prestação criada com sucesso!');
        navigate('/prestacoes');
      }
    } catch (error: any) {
      console.error('Error creating prestacao:', error);
      setSalvando(false);
      
      if (error.message?.includes('UNIQUE constraint') || error.message?.includes('UNIQUE')) {
        setErrors({ 
          mes: `Já existe uma prestação para ${formData.mes}/${formData.ano} desta unidade` 
        });
      } else {
        alert('Erro ao criar prestação de contas. Tente novamente.');
      }
      // NÃO redirecionar se houver erro
      return;
    }
  };

  const getMesNome = (mes: number) => {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return meses[mes - 1];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/prestacoes"
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar para Prestações</span>
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Nova Prestação de Contas
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Preencha os dados para criar uma nova prestação
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Unidade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4" />
                  <span>Unidade *</span>
                </div>
              </label>
              <select
                value={formData.unidade_id}
                onChange={(e) => handleUnidadeChange(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.unidade_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Selecione uma unidade</option>
                {unidades.map((unidade) => (
                  <option key={unidade.id} value={unidade.id}>
                    {unidade.nome} - {unidade.codigo}
                  </option>
                ))}
              </select>
              {errors.unidade_id && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.unidade_id}</p>
              )}
              
              {/* Exibir CNPJ da unidade selecionada */}
              {selectedUnidade && selectedUnidade.cnpj && (
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-800 dark:text-blue-200 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-medium">CNPJ da Empresa:</span>
                    <span className="font-mono">{selectedUnidade.cnpj}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Ano e Mês */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Ano *</span>
                  </div>
                </label>
                <input
                  type="number"
                  min="2020"
                  max="2030"
                  value={formData.ano}
                  onChange={(e) => setFormData({ ...formData, ano: parseInt(e.target.value) || new Date().getFullYear() })}
                  className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.ano ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.ano && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.ano}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Mês *</span>
                  </div>
                </label>
                <select
                  value={formData.mes}
                  onChange={(e) => setFormData({ ...formData, mes: parseInt(e.target.value) })}
                  className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.mes ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((mes) => (
                    <option key={mes} value={mes}>
                      {getMesNome(mes)}
                    </option>
                  ))}
                </select>
                {errors.mes && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.mes}</p>
                )}
              </div>
            </div>

            {/* Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Nome do Responsável *</span>
                </div>
              </label>
              <input
                type="text"
                value={formData.responsavel_nome}
                onChange={(e) => setFormData({ ...formData, responsavel_nome: e.target.value })}
                placeholder="Digite o nome do responsável"
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.responsavel_nome ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.responsavel_nome && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.responsavel_nome}</p>
              )}
            </div>

            {/* CPF do Responsável (mantido por compatibilidade) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>CPF/Documento do Responsável *</span>
                </div>
              </label>
              <input
                type="text"
                value={formData.responsavel_cpf}
                onChange={(e) => setFormData({ ...formData, responsavel_cpf: e.target.value })}
                placeholder="000.000.000-00"
                maxLength={14}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.responsavel_cpf ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.responsavel_cpf && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.responsavel_cpf}</p>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                CPF do responsável pela prestação de contas (Formato: 000.000.000-00)
              </p>
            </div>

            {/* Botões */}
            <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={salvando}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {salvando ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Criar Prestação de Contas</span>
                  </>
                )}
              </button>
              <Link
                to="/prestacoes"
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </Link>
            </div>
          </form>
        </div>

        {/* Informações adicionais */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
            Informações Importantes
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
            <li>A prestação será criada com status "Aberto" por padrão</li>
            <li>Você poderá adicionar documentos fiscais após criar a prestação</li>
            <li>Não é possível criar duas prestações para a mesma unidade, ano e mês</li>
            <li>O responsável será associado à sua conta de usuário</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
